import { SubmitHandler, useForm } from 'react-hook-form'
import { useNavigate } from 'react-router-dom'

import { zodResolver } from '@hookform/resolvers/zod'

import {
  usePermissionTemplateCreateApi,
  usePermissionTemplateUpdateApi
} from '@/common/api/modules/permissionTemplate'
import { useFormErrorMessages, useMessageStates } from '@/common/app/message'
import { permissionTemplateEditPath } from '@/common/app/path'
import { useSiteId } from '@/common/app/site'
import { useShowSavedToast } from '@/common/app/toast'
import { PermissionTemplate } from '@/common/model/auth'
import { preventKeyDown } from '@/common/util/keyboard'

import Badge, { BadgeType } from '@/components/view/atoms/badge/Badge'
import Button, { ButtonColor } from '@/components/view/atoms/button/Button'
import { CheckBoxType } from '@/components/view/atoms/checkbox/CheckBox'
import FormLabel from '@/components/view/atoms/form/FormLabel'
import Heading from '@/components/view/atoms/heading/Heading'
import { FieldType, TextBoxSize } from '@/components/view/atoms/textfield/TextField'
import Area from '@/components/view/layouts/area/Area'
import AreaContent from '@/components/view/layouts/area/AreaContent'
import FormRow from '@/components/view/layouts/form/FormRow'
import Content from '@/components/view/layouts/page/Content'
import PageBottom from '@/components/view/layouts/page/PageBottom'
import TopBar, { TopBarStatus } from '@/components/view/molecules/bar/TopBar'
import PageTitle from '@/components/view/molecules/title/PageTitle'

import {
  DEFAULT_PERMISSION_TEMPLATE_SCHEMA,
  PERMISSION_MENU_KEYS,
  PermissionTemplateFormSchemaType,
  convertPermissionTemplateSchemaToVars,
  convertPermissionTemplateToSchema,
  permissionTemplateNameLabelMapping,
  permissionTemplateSchema
} from '@/components/container/form/modules/site/permission/permissionTemplateSchema'
import FormCheckBox from '@/components/container/form/widgets/FormCheckBox'
import FormError from '@/components/container/form/widgets/FormError'
import FormMessageList from '@/components/container/form/widgets/FormMessageList'
import FormTextField, { FormTextFieldType } from '@/components/container/form/widgets/FormTextField'
import { useAppHeaderHiding } from '@/components/container/layout/AppHeaderProvider'

interface Props {
  template?: PermissionTemplate
  onRefetch?(): void
}

const PermissionTemplateForm = ({ template, onRefetch }: Props) => {
  const navigate = useNavigate()
  const siteId = useSiteId()

  const {
    register,
    control,
    handleSubmit,
    formState: { errors, isDirty, isSubmitting },
    reset
  } = useForm<PermissionTemplateFormSchemaType>({
    resolver: zodResolver(permissionTemplateSchema),
    values: template
      ? convertPermissionTemplateToSchema(template)
      : DEFAULT_PERMISSION_TEMPLATE_SCHEMA,
    mode: 'onChange'
  })

  useAppHeaderHiding(isDirty)
  const [messages, setMessages] = useMessageStates()
  useFormErrorMessages(errors, setMessages, permissionTemplateNameLabelMapping)
  const showSaved = useShowSavedToast({ editing: !!template })

  const onSuccess = (response: PermissionTemplate) => {
    showSaved(response.name)
    template && onRefetch ? onRefetch() : navigate(permissionTemplateEditPath(siteId, response.id))
  }
  const { trigger: create } = usePermissionTemplateCreateApi({ onSuccess })
  const { trigger: update } = usePermissionTemplateUpdateApi({ onSuccess })

  const onSubmit: SubmitHandler<PermissionTemplateFormSchemaType> = (data) => {
    const vars = convertPermissionTemplateSchemaToVars(siteId, data)
    // template_id の有無で Update/Create を型ナローイング（vars は両者の union）
    return 'template_id' in vars ? update(vars) : create(vars)
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)} onKeyDown={preventKeyDown}>
      <PageTitle
        title={template ? template.name : '権限テンプレート新規作成'}
        behindNode={
          template?.is_system && <Badge badgeType={BadgeType.SECONDARY}>システム標準</Badge>
        }
        rightNode={
          <Button
            type='submit'
            size='large'
            color={ButtonColor.PRIMARY_BRAND}
            loading={isSubmitting}
          >
            {template ? '保存する' : '登録する'}
          </Button>
        }
      />
      {isDirty && (
        <TopBar
          status={template ? TopBarStatus.CHANGE : TopBarStatus.NEW}
          captionText={template ? '未反映の変更があります' : '登録されていない新しいテンプレート'}
          onCancel={() =>
            reset(
              template
                ? convertPermissionTemplateToSchema(template)
                : DEFAULT_PERMISSION_TEMPLATE_SCHEMA
            )
          }
          loading={isSubmitting}
        />
      )}
      <FormMessageList messageStates={messages} setMessageStates={setMessages} />
      <Content>
        <Area noBorder>
          <Heading>基本情報</Heading>
          <AreaContent>
            <FormRow>
              <FormLabel required id='name'>
                テンプレート名
              </FormLabel>
              <FormRow direction={'vertical'} gap={2}>
                <FormTextField
                  register={register('name')}
                  fieldType={FieldType.REQUIRED}
                  error={!!errors.name?.message}
                  placeholder='編集者'
                />
                <FormError message={errors.name?.message} />
              </FormRow>
            </FormRow>
          </AreaContent>
        </Area>
        <Area noBorder>
          <Heading>メニュー利用権限</Heading>
          <AreaContent>
            {PERMISSION_MENU_KEYS.map(({ key, label }) => (
              <FormRow key={key}>
                <FormLabel>{label}</FormLabel>
                <FormCheckBox
                  control={control}
                  name={`menuPermissions.${key}`}
                  type={CheckBoxType.TOGGLE_TEXT}
                  toggleText={{ left: 'ON', right: 'OFF' }}
                />
              </FormRow>
            ))}
          </AreaContent>
        </Area>
        <Area noBorder>
          <Heading>その他の権限</Heading>
          <AreaContent>
            <FormRow>
              <FormLabel>記事更新の承認</FormLabel>
              <FormCheckBox
                control={control}
                name={'canApproveArticle'}
                type={CheckBoxType.TOGGLE_TEXT}
                toggleText={{ left: 'ON', right: 'OFF' }}
              />
            </FormRow>
          </AreaContent>
        </Area>
      </Content>
      <PageBottom>
        <Button type='submit' size='large' color={ButtonColor.PRIMARY_BRAND} loading={isSubmitting}>
          {template ? '保存する' : '登録する'}
        </Button>
      </PageBottom>
    </form>
  )
}

export default PermissionTemplateForm
